import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ordo/ui/card'
import { Skeleton } from '@ordo/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ordo/ui/table'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import {
  type CustomerioImport,
  useCancelCustomerioImport,
  useCustomerioImports,
  useStartCustomerioImport,
} from 'hooks/api'
import { Download, Square } from 'lucide-react'
import { useCallback } from 'react'

function statusVariant(status: CustomerioImport['status']): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'COMPLETED':
      return 'default'
    case 'RUNNING':
      return 'secondary'
    case 'FAILED':
      return 'destructive'
    default:
      return 'outline'
  }
}

function countsSummary(counts: Record<string, number>): string {
  const parts: string[] = []
  for (const [k, v] of Object.entries(counts)) {
    if (v > 0) {
      parts.push(`${v} ${k}`)
    }
  }
  return parts.length > 0 ? parts.join(' · ') : '—'
}

export function CustomerioImportsSection() {
  const { data, loading, refetch } = useCustomerioImports()
  const { perform: start, loading: starting } = useStartCustomerioImport()
  const { perform: cancel, loading: cancelling } = useCancelCustomerioImport()

  // Polling while an import is RUNNING lives in useCustomerioImports
  // (TanStack refetchInterval) — no interval needed here.

  const handleStart = useCallback(async () => {
    await start()
    refetch()
  }, [start, refetch])

  const handleCancel = useCallback(
    async (id: string) => {
      await cancel(id)
      refetch()
    },
    [cancel, refetch],
  )

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Customer.io Import</CardTitle>
          <CardDescription>
            Pull templates, layouts, snippets, and campaigns from Customer.io. Campaigns land as disabled schedules —
            enable manually after review.
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleStart} disabled={starting}>
            <Download className="mr-1 h-3 w-3" />
            New Import
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <LoadingWrapper loading={loading} loadingElement={<Skeleton className="h-20 w-full" />}>
          {data?.length === 0 ? (
            <p className="text-sm text-muted-foreground">No imports yet. Kick off a new import to begin.</p>
          ) : null}
          {data && data.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Phase</TableHead>
                  <TableHead>Counts</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((imp) => (
                  <TableRow key={imp.id}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {new Date(imp.startedAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(imp.status)}>{imp.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{imp.phase ?? '—'}</TableCell>
                    <TableCell className="text-sm">{countsSummary(imp.counts)}</TableCell>
                    <TableCell className="text-right text-sm">
                      {imp.errors.length > 0 ? imp.errors.length : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {imp.status === 'RUNNING' ? (
                        <Button variant="ghost" size="sm" onClick={() => handleCancel(imp.id)} disabled={cancelling}>
                          <Square className="mr-1 h-3 w-3" />
                          Cancel
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </LoadingWrapper>
      </CardContent>
    </Card>
  )
}
